'use client';

import { c as _c } from "react/compiler-runtime";
import { type UpdateSession, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { LOGIN_PATH } from '@module/mdrt-org/shared/utils/constants/local-paths';
import { isServerReachable } from '@module/mdrt-org/shared/utils/helpers/is-server-reachable';
import { ADB2CUserGroup } from '@shared/misc/next-auth/enum';
type UserContextValue = {
  accessToken: string | undefined;
  authenticatedPersonId: number | undefined;
  groups: string[] | undefined;
  hasCompanyAdminRole: boolean | undefined;
  hasStaffRole: boolean | undefined;
  isAuthenticated: boolean;
  personId: number | undefined;
  personIdBeforeImpersonation: number | undefined;
  status: 'authenticated' | 'loading' | 'unauthenticated';
  updateSession: UpdateSession;
};
const AUTH_RECONCILE_LATCH_KEY = 'mdrt.auth-reconcile-refreshed';
const readReconcileLatch = () => {
  try {
    return sessionStorage.getItem(AUTH_RECONCILE_LATCH_KEY) === 'true';
  } catch {
    return false;
  }
};
const writeReconcileLatch = (isLatched: boolean) => {
  try {
    sessionStorage.setItem(AUTH_RECONCILE_LATCH_KEY, String(isLatched));
    return true;
  } catch {
    return false;
  }
};
const UserContext = createContext<undefined | UserContextValue>(undefined);
const ServerAuthStateContext = createContext<((wasAuthenticated: boolean) => void) | undefined>(undefined);
export const UserContextProvider = t0 => {
  const $ = _c(39);
  const {
    children
  } = t0;
  const {
    data,
    status,
    update: updateSession
  } = useSession();
  const router = useRouter();
  const wasAuthenticatedRef = useRef(false);
  const statusRef = useRef(status);
  const serverAuthStateReportedRef = useRef(false);
  const isReconcilingRef = useRef(false);
  let t1;
  if ($[0] !== router) {
    t1 = isReachable => {
      isReconcilingRef.current = false;
      if (!isReachable) {
        return;
      }
      wasAuthenticatedRef.current = false;
      writeReconcileLatch(true);
      router.refresh();
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const completeReconcile = t1;
  let t2;
  if ($[2] !== completeReconcile) {
    t2 = async () => {
      const currentStatus = statusRef.current;
      if (currentStatus === "loading") {
        return;
      }
      if (currentStatus === "authenticated") {
        wasAuthenticatedRef.current = true;
        writeReconcileLatch(false);
        return;
      }
      if (!wasAuthenticatedRef.current || isReconcilingRef.current || readReconcileLatch()) {
        return;
      }
      isReconcilingRef.current = true;
      completeReconcile(await isServerReachable());
    };
    $[2] = completeReconcile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const reconcileAuthState = t2;
  let t3;
  if ($[4] !== reconcileAuthState) {
    t3 = wasAuthenticated => {
      if (serverAuthStateReportedRef.current || !wasAuthenticated) {
        return;
      }
      serverAuthStateReportedRef.current = true;
      wasAuthenticatedRef.current = true;
      reconcileAuthState();
    };
    $[4] = reconcileAuthState;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const reportServerAuthState = t3;
  const groups = data?.groups;
  const isAuthenticated = status === "authenticated" && !data?.error;
  let t4;
  if ($[6] !== groups || $[7] !== isAuthenticated) {
    t4 = isAuthenticated && groups?.includes(ADB2CUserGroup.MDRTStaff);
    $[6] = groups;
    $[7] = isAuthenticated;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const hasStaffRole = t4;
  let t5;
  if ($[9] !== groups || $[10] !== isAuthenticated) {
    t5 = isAuthenticated && groups?.includes(ADB2CUserGroup.CompanyAdmin);
    $[9] = groups;
    $[10] = isAuthenticated;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const hasCompanyAdminRole = t5;
  data?.personIdBeforeImpersonation;
  data?.accessToken;
  data?.personId;
  const t6 = data?.accessToken;
  const t7 = data?.personIdBeforeImpersonation ?? data?.personId;
  const t8 = data?.personId;
  const t9 = data?.personIdBeforeImpersonation;
  let t10;
  if ($[12] !== groups || $[13] !== hasCompanyAdminRole || $[14] !== hasStaffRole || $[15] !== isAuthenticated || $[16] !== status || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9 || $[21] !== updateSession) {
    t10 = {
      accessToken: t6,
      authenticatedPersonId: t7,
      groups,
      hasCompanyAdminRole,
      hasStaffRole,
      isAuthenticated,
      personId: t8,
      personIdBeforeImpersonation: t9,
      status,
      updateSession
    };
    $[12] = groups;
    $[13] = hasCompanyAdminRole;
    $[14] = hasStaffRole;
    $[15] = isAuthenticated;
    $[16] = status;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = updateSession;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const value = t10;
  let t11;
  if ($[23] !== data?.error || $[24] !== router) {
    t11 = () => {
      if (data?.error) {
        router.push(LOGIN_PATH);
      }
    };
    $[23] = data?.error;
    $[24] = router;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = data?.error;
  let t13;
  if ($[26] !== router || $[27] !== t12) {
    t13 = [t12, router];
    $[26] = router;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  useEffect(t11, t13);
  let t14;
  let t15;
  if ($[29] !== reconcileAuthState || $[30] !== status) {
    t14 = () => {
      statusRef.current = status;
      reconcileAuthState();
    };
    t15 = [reconcileAuthState, status];
    $[29] = reconcileAuthState;
    $[30] = status;
    $[31] = t14;
    $[32] = t15;
  } else {
    t14 = $[31];
    t15 = $[32];
  }
  useEffect(t14, t15);
  let t16;
  if ($[33] !== children || $[34] !== value) {
    t16 = <UserContext.Provider value={value}>{children}</UserContext.Provider>;
    $[33] = children;
    $[34] = value;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== reportServerAuthState || $[37] !== t16) {
    t17 = <ServerAuthStateContext.Provider value={reportServerAuthState}>{t16}</ServerAuthStateContext.Provider>;
    $[36] = reportServerAuthState;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
};
export const useUserContext = () => {
  const value = useContext(UserContext);
  if (!value) {
    throw new Error(`${useUserContext.name} must be used inside of ${UserContextProvider.name}`);
  }
  return value;
};
export const useReportServerAuthState = () => {
  const reportServerAuthState = useContext(ServerAuthStateContext);
  if (!reportServerAuthState) {
    throw new Error(`${useReportServerAuthState.name} must be used inside of ${UserContextProvider.name}`);
  }
  return reportServerAuthState;
};